import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconCheck, IconDoc, IconExternal, IconPhone, IconRides } from '../components/icons';
import {
  Dialog,
  DriverStatusPill,
  Empty,
  ErrorNote,
  FlashNote,
  Loading,
  Note,
  PageHead,
  ReasonDialog,
  useFlash,
  VehicleTile,
} from '../components/ui';
import { api } from '../lib/api';
import { ago, dateTime, day, DOCUMENT_LABEL, phone, rupees, VEHICLE_LABEL } from '../lib/format';
import { qk } from '../lib/realtime-cache';
import type { AdminRide, DocumentKind, DriverDetail, DriverDocument, Page, RatingPage } from '../lib/types';
import { driverRoute } from '../router';
import { Stars, TAG_LABEL } from './Ratings';
import { RideTable } from './Rides';

type Action = 'approve' | 'reject' | 'suspend';

const ACTION_COPY: Record<Action, { title: string; body: string; confirm: string; done: string; danger?: boolean; required: boolean }> = {
  approve: {
    title: 'Approve this driver?',
    body: 'They can go online and receive rides straight away.',
    confirm: 'Approve',
    done: 'Driver approved.',
    required: false,
  },
  reject: {
    title: 'Reject this driver?',
    body: 'The driver sees your reason in the app and can fix things and send again.',
    confirm: 'Reject',
    done: 'Driver rejected.',
    danger: true,
    required: true,
  },
  suspend: {
    title: 'Suspend this driver?',
    body: 'They go offline at once and get no new rides. A ride already in progress is not cancelled — cancel it separately if needed.',
    confirm: 'Suspend',
    done: 'Driver suspended.',
    danger: true,
    required: true,
  },
};

const DOC_ORDER: DocumentKind[] = ['DRIVING_LICENCE', 'VEHICLE_RC', 'AADHAAR'];
const CHECKS = ['NAME', 'VEHICLE', ...DOC_ORDER];

/**
 * A document's picture, fetched through CORS from its 5-minute signed link (the file server only allows same-origin
 * <img> loads) and shown from a local blob URL. PDFs show a document tile instead.
 */
function useDocBlob(doc: DriverDocument | undefined) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const url = doc?.url;
  const isImage = !!doc && doc.mimeType.startsWith('image/');
  useEffect(() => {
    if (!url || !isImage) return;
    let alive = true;
    let made: string | null = null;
    fetch(url)
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => {
        if (!alive) return;
        made = URL.createObjectURL(b);
        setSrc(made);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [url, isImage]);
  return { src, failed, isImage };
}

function DocCard({ kind, doc, onOpen, onView }: { kind: DocumentKind; doc?: DriverDocument; onOpen: () => void; onView: (src: string) => void }) {
  const { src, isImage } = useDocBlob(doc);
  return (
    <div className="doc" data-testid={`doc-${kind}`}>
      {doc ? (
        <button
          type="button"
          className="doc-thumb"
          aria-label={`View ${DOCUMENT_LABEL[kind]}`}
          onClick={() => (src ? onView(src) : onOpen())}
        >
          {src ? <img src={src} alt="" /> : isImage ? <div className="skeleton" style={{ position: 'absolute', inset: 0, borderRadius: 0, height: 'auto' }} /> : <IconDoc />}
        </button>
      ) : (
        <div className="doc-thumb missing" aria-label={`${DOCUMENT_LABEL[kind]} not uploaded`}>
          <IconDoc />
        </div>
      )}
      <div className="doc-info">
        <div style={{ minWidth: 0 }}>
          <div className="strong truncate">{DOCUMENT_LABEL[kind]}</div>
          <div className="small muted">{doc ? `${doc.mimeType === 'application/pdf' ? 'PDF' : 'Photo'} · ${day(doc.uploadedAt)}` : 'Not uploaded'}</div>
        </div>
        {doc && (
          <button className="btn btn-sm" onClick={onOpen}>
            Open
          </button>
        )}
      </div>
    </div>
  );
}

export function DriverDetailPage() {
  const { driverId } = driverRoute.useParams();
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const [action, setAction] = useState<Action | null>(null);
  const [viewing, setViewing] = useState<{ kind: DocumentKind; src: string } | null>(null);
  const q = useQuery({ queryKey: qk.driver(driverId), queryFn: () => api.get<DriverDetail>(`/admin/drivers/${driverId}`) });
  const rides = useQuery({
    queryKey: [...qk.rides, { driverId }],
    queryFn: () => api.get<Page<AdminRide>>('/admin/rides', { driverId, pageSize: 10 }),
  });
  const ratings = useQuery({
    queryKey: [...qk.ratings, { driverId }],
    queryFn: () => api.get<RatingPage>('/admin/ratings', { driverId, byType: 'RIDER', pageSize: 4 }),
  });

  if (q.isLoading) return <Loading rows={6} />;
  if (q.error || !q.data)
    return (
      <div className="stack">
        <PageHead title="Driver" back={{ to: '/drivers', label: 'Drivers' }} />
        <ErrorNote error={q.error} />
      </div>
    );
  const d = q.data;

  /** Signed links last 5 minutes — always fetch a fresh one when opening a document. */
  const openDocument = async (kind: DocumentKind) => {
    const w = window.open('', '_blank');
    try {
      const fresh = await qc.fetchQuery({
        queryKey: qk.driver(driverId),
        queryFn: () => api.get<DriverDetail>(`/admin/drivers/${driverId}`),
        staleTime: 0,
      });
      const url = fresh.documents.find((x) => x.kind === kind)?.url;
      if (!url) throw new Error('Document not found');
      if (w) {
        w.opener = null;
        w.location.href = url;
      } else window.location.href = url;
    } catch {
      w?.close();
      show('Could not open the document. Try again.', 'bad');
    }
  };

  const can = {
    approve: ['PENDING', 'REJECTED', 'SUSPENDED'].includes(d.status),
    reject: d.status === 'PENDING',
    suspend: ['APPROVED', 'PENDING'].includes(d.status),
  };
  const approveBlocked = d.missing.length > 0;
  const actions = (
    <>
      {can.reject && (
        <button className="btn btn-danger" onClick={() => setAction('reject')}>
          Reject
        </button>
      )}
      {can.suspend && (
        <button className="btn btn-danger" onClick={() => setAction('suspend')}>
          Suspend
        </button>
      )}
      {can.approve && (
        <button
          className="btn btn-primary"
          disabled={approveBlocked}
          title={approveBlocked ? 'Needs every document and the vehicle first' : undefined}
          onClick={() => setAction('approve')}
        >
          {d.status === 'SUSPENDED' ? 'Lift suspension' : 'Approve'}
        </button>
      )}
    </>
  );
  const hasActions = can.approve || can.reject || can.suspend;
  const reviewing = d.status === 'PENDING';

  return (
    <div className={`stack${hasActions ? ' has-action-bar' : ''}`}>
      <PageHead
        back={{ to: '/drivers', label: 'Drivers' }}
        title={
          <>
            {d.name ?? 'No name yet'} <DriverStatusPill status={d.status} />
          </>
        }
        sub={[d.phone ? phone(d.phone) : 'No phone yet', d.email, `joined ${day(d.createdAt)}`].filter(Boolean).join(' · ')}
        actions={hasActions ? <span className="page-actions hide-phone">{actions}</span> : undefined}
      />

      <FlashNote flash={flash} />
      {d.statusReason && (
        <Note tone={d.status === 'APPROVED' ? 'neutral' : 'warn'}>
          <strong>Note the driver sees:</strong> {d.statusReason}
        </Note>
      )}
      {d.missing.length > 0 && (
        <Note tone="warn">
          <strong>Can’t approve yet.</strong> Missing: {d.missing.map((m) => DOCUMENT_LABEL[m] ?? m).join(', ')}.
        </Note>
      )}

      <section className="card" aria-label="Documents">
        <div className="card-head">
          <h2>{reviewing ? 'Review documents' : 'Documents'}</h2>
          <span className="small faint">Private links, 5 minutes each</span>
        </div>
        <div className="card-body">
          <div className="grid-detail" style={{ gridTemplateColumns: reviewing ? undefined : 'minmax(0,1fr)' }}>
            <div className="docs">
              {DOC_ORDER.map((kind) => (
                <DocCard
                  key={kind}
                  kind={kind}
                  doc={d.documents.find((x) => x.kind === kind)}
                  onOpen={() => void openDocument(kind)}
                  onView={(src) => setViewing({ kind, src })}
                />
              ))}
            </div>
            {reviewing && (
              <div className="stack-sm">
                <span className="overline">Before you approve</span>
                <ul className="checklist">
                  {CHECKS.map((c) => {
                    const ok = !d.missing.includes(c);
                    return (
                      <li key={c}>
                        <span className={`tick${ok ? '' : ' no'}`}>{ok ? <IconCheck /> : '!'}</span>
                        {DOCUMENT_LABEL[c]}
                        {c === 'VEHICLE' && d.vehicle ? <span className="muted"> · {d.vehicle.plateDisplay}</span> : ''}
                      </li>
                    );
                  })}
                </ul>
                <p className="small muted">Check the name and photo match on the licence and Aadhaar, and the plate matches the RC.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="grid-3">
        <section className="card card-pad stack-sm">
          <span className="overline">Driver</span>
          <dl className="kv">
            <dt>Online</dt>
            <dd>{d.isOnline ? `Yes · seen ${ago(d.location?.at)}` : 'No'}</dd>
            <dt>Rating</dt>
            <dd>{d.rating != null ? `★ ${d.rating.toFixed(1)}` : 'No ratings yet'}</dd>
            <dt>Rides done</dt>
            <dd className="num">{d.completedRides}</dd>
            <dt>Balance</dt>
            <dd className="num">
              <span className="strong">{rupees(d.balancePaise)}</span>
              <div className="small faint">
                {d.balancePaise > 0 ? 'Zoomo Rides owes the driver' : d.balancePaise < 0 ? 'Driver owes Zoomo Rides' : 'Settled'}
              </div>
            </dd>
          </dl>
          <Link to="/payouts/$driverId" params={{ driverId: d.id }} className="btn btn-sm btn-tonal">
            {d.balancePaise !== 0 ? 'Settle or pay' : 'Money and statements'}
          </Link>
          {d.phone ? (
            <a className="call-btn" href={`tel:${d.phone}`}>
              <IconPhone /> Call driver
            </a>
          ) : (
            <p className="small muted">No mobile number yet — the driver can’t go online until they add one.</p>
          )}
        </section>

        <section className="card card-pad stack-sm">
          <span className="overline">Vehicle</span>
          {d.vehicle ? (
            <>
              <div className="person">
                <VehicleTile type={d.vehicle.type} />
                <div>
                  <div className="strong">{VEHICLE_LABEL[d.vehicle.type]}</div>
                  <div className="small muted">{d.vehicle.plateDisplay}</div>
                </div>
              </div>
              <dl className="kv">
                <dt>Make</dt>
                <dd>
                  {d.vehicle.make} {d.vehicle.model}
                </dd>
                <dt>Colour</dt>
                <dd>{d.vehicle.colour}</dd>
              </dl>
            </>
          ) : (
            <p className="muted">Not added yet.</p>
          )}
        </section>

        <section className="card card-pad stack-sm">
          <div className="spread">
            <span className="overline">What riders say</span>
            <Link to="/ratings" search={{ show: 'all', by: 'RIDER' }} className="link small">
              All ratings
            </Link>
          </div>
          {ratings.data?.items.length ? (
            ratings.data.items.map((x) => (
              <div key={x.id} className="stack-xs">
                <div className="row" style={{ gap: 8 }}>
                  <Stars n={x.stars} />
                  <span className="small faint">{dateTime(x.createdAt)}</span>
                </div>
                {(x.tags.length > 0 || x.comment) && (
                  <div className="small muted">
                    {x.tags.map((t) => TAG_LABEL[t] ?? t).join(', ')}
                    {x.comment ? ` · “${x.comment}”` : ''}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="muted">No ratings yet.</p>
          )}
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <h2>Recent rides</h2>
          <span className="small muted">Last 10</span>
        </div>
        {rides.isLoading ? (
          <Loading rows={2} />
        ) : rides.data?.items.length ? (
          <RideTable rides={rides.data.items} />
        ) : (
          <Empty title="No rides yet" icon={IconRides} />
        )}
      </section>

      {hasActions && <div className="action-bar only-phone">{actions}</div>}

      {viewing && (
        <Dialog title={DOCUMENT_LABEL[viewing.kind]} onClose={() => setViewing(null)} wide>
          <div className="stack-sm">
            <div className="viewer">
              <img src={viewing.src} alt={`${DOCUMENT_LABEL[viewing.kind]} of ${d.name ?? 'the driver'}`} />
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => void openDocument(viewing.kind)}>
                <IconExternal /> Open in a new tab
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {action && (
        <ReasonDialog
          title={ACTION_COPY[action].title}
          body={ACTION_COPY[action].body}
          confirmLabel={ACTION_COPY[action].confirm}
          danger={ACTION_COPY[action].danger}
          required={ACTION_COPY[action].required}
          placeholder={action === 'reject' ? 'For example: RC photo is blurred — please upload again' : undefined}
          onClose={() => setAction(null)}
          onConfirm={async (reason) => {
            await api.post(`/admin/drivers/${d.id}/${action}`, reason ? { reason } : {});
            show(ACTION_COPY[action].done);
            void qc.invalidateQueries({ queryKey: qk.driver(d.id) });
            void qc.invalidateQueries({ queryKey: qk.drivers });
            void qc.invalidateQueries({ queryKey: qk.liveDrivers });
            void qc.invalidateQueries({ queryKey: qk.dashboard });
          }}
        />
      )}
    </div>
  );
}

